import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { CheckoutOverlay } from "@/components/CheckoutOverlay";
import { resolveReveal } from "@/lib/reveal";
import { ArrowLeft, Copy, Video, Film, PlayCircle, CheckCircle2, Mail, Send, Loader2, Palette, Lock, Check, Sparkles, Share2 } from "lucide-react";
import RevealAnimation from "@/components/RevealAnimation";

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [reactions, setReactions] = useState([]);
  const [playing, setPlaying] = useState(null);
  const [emails, setEmails] = useState("");
  const [sending, setSending] = useState(false);
  const [themes, setThemes] = useState([]);
  const [preview, setPreview] = useState(null);
  const [buying, setBuying] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [hlStatus, setHlStatus] = useState("none");
  const [hlUrl, setHlUrl] = useState("");

  const load = () => api.get(`/events/${id}`).then((r) => setEvent(r.data)).catch(() => toast.error("Event not found"));
  useEffect(() => { load(); }, [id]);
  useEffect(() => { api.get("/animation-themes").then((r) => setThemes(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (event) { setHlStatus(event.highlight_status || "none"); setHlUrl(event.highlight_url || ""); } }, [event]);
  useEffect(() => {
    if (hlStatus !== "processing") return;
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get(`/events/${id}/highlight`);
        setHlStatus(data.status);
        if (data.highlight_url) setHlUrl(data.highlight_url);
        if (data.status === "ready") toast.success("Your highlight reel is ready!");
        if (data.status === "failed") toast.error("Highlight reel failed to build — please try again.");
      } catch { /* keep polling */ }
    }, 4000);
    return () => clearInterval(iv);
  }, [hlStatus, id]);

  const generateHighlight = async () => {
    try {
      setHlStatus("processing");
      await api.post(`/events/${id}/highlight`);
      toast.success("Building your highlight reel… this can take a minute.");
    } catch (err) {
      setHlStatus(event?.highlight_status || "none");
      toast.error(err.response?.data?.detail || "Could not start the highlight reel");
    }
  };
  const copyHighlight = async () => {
    try { await navigator.clipboard.writeText(hlUrl); toast.success("Highlight reel link copied!"); }
    catch { toast.error("Couldn't copy the link"); }
  };
  const shareReel = async () => {
    const shareUrl = `${window.location.origin}/api/reel/${event.share_code}`;
    if (navigator.share) {
      try { await navigator.share({ title: `${event.title} — reveal reel`, text: "Watch everyone's reactions!", url: shareUrl }); return; }
      catch { /* fall back to copy */ }
    }
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Public share link copied — post it anywhere!"); }
    catch { toast.error("Couldn't copy the link"); }
  };

  const applyFreeTheme = async (themeId) => {
    try {
      const { data } = await api.post(`/events/${event.id}/animation`, { theme: themeId });
      setEvent(data);
      toast.success("Reveal style updated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update style");
    }
  };
  const unlockTheme = async (themeId) => {
    setBuying(true);
    try {
      const { data } = await api.post(`/events/${event.id}/animation/checkout`, { theme: themeId });
      setCheckoutUrl(data.checkout_url);
      setBuying(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout");
      setBuying(false);
    }
  };
  useEffect(() => {
    if (!event) return;
    api.get(`/public/events/${event.share_code}/reactions`).then((r) => setReactions(r.data)).catch(() => {});
  }, [event]);

  if (!event) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;

  const shareUrl = `${window.location.origin}/reveal/${event.share_code}`;
  const rv = resolveReveal(event);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Invite link copied!");
    } catch {
      toast.error("Copy failed — select the link manually");
    }
  };

  const sendInvites = async () => {
    const list = emails.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);
    if (list.length === 0) return toast.error("Add at least one email");
    setSending(true);
    try {
      const { data } = await api.post(`/events/${event.id}/invite`, { emails: list });
      toast.success(`Sent ${data.sent.length} invite${data.sent.length !== 1 ? "s" : ""}!`);
      if (data.failed?.length) toast.error(`Failed for: ${data.failed.join(", ")}`);
      setEmails("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send invites");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <button data-testid="back-btn" onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-foreground/60 hover:text-white transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </button>

        <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
          <div>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight">{event.title}</h1>
            <p className="mt-2 text-foreground/50">
              {event.scheduled_at ? new Date(event.scheduled_at).toLocaleString() : "No date set"} ·{" "}
              {event.revealed ? <span className="text-forest">Revealed</span> : <span>Pending reveal</span>}
            </p>
          </div>
          <Button data-testid="enter-room-btn" onClick={() => navigate(`/reveal/${event.share_code}`)} className="rounded-full h-12 px-6 bg-gold hover:bg-gold/90 font-semibold">
            <Video className="w-4 h-4 mr-2" /> Enter reveal room
          </Button>
        </div>

        {/* secret card (host only) */}
        <div className="rounded-2xl border border-white/10 p-6 mb-6" style={{ background: `${rv.color}14` }}>
          <p className="text-xs uppercase tracking-widest text-foreground/60 mb-1">Your secret (only you can see this)</p>
          <p className="font-display font-black text-3xl" style={{ color: rv.color }}>{rv.headline}</p>
        </div>

        {/* invite link */}
        <div className="rounded-2xl bg-card border border-white/5 p-6 mb-6">
          <p className="text-xs uppercase tracking-widest text-foreground/60 mb-3">Invite friends & family</p>
          <div className="flex gap-2">
            <input data-testid="share-link-input" readOnly value={shareUrl} className="flex-1 h-12 px-4 rounded-xl bg-input border border-white/10 text-sm text-foreground/80" />
            <Button data-testid="copy-link-btn" onClick={copy} className="h-12 px-5 rounded-xl bg-white text-black hover:scale-105 transition-transform"><Copy className="w-4 h-4" /></Button>
          </div>
          <p className="mt-3 text-sm text-foreground/50">Anyone with this link can join the live video call. Only you can press the reveal button.</p>
        </div>

        {/* email invites */}
        <div className="rounded-2xl bg-card border border-white/5 p-6 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Mail className="w-5 h-5 text-gold" />
            <p className="text-xs uppercase tracking-widest text-foreground/60">Send email invitations</p>
          </div>
          <textarea
            data-testid="invite-emails-input"
            value={emails}
            onChange={(e) => setEmails(e.target.value)}
            placeholder="mom@email.com, dad@email.com, bestie@email.com"
            rows={3}
            className="w-full p-4 rounded-xl bg-input border border-white/10 text-sm text-foreground/90 focus:border-white/40 outline-none resize-none"
          />
          <div className="flex items-center justify-between mt-3">
            <p className="text-sm text-foreground/50">We'll email everyone a beautiful invite with the date, time & join link.</p>
            <Button data-testid="send-invites-btn" onClick={sendInvites} disabled={sending} className="h-11 px-5 rounded-xl bg-gold hover:bg-gold/90 font-semibold shrink-0">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Send</>}
            </Button>
          </div>
        </div>

        {/* animation store */}
        <div className="rounded-2xl bg-card border border-white/5 p-6 mb-6" data-testid="animation-store">
          <div className="flex items-center gap-2 mb-4">
            <Palette className="w-5 h-5 text-gold" />
            <h2 className="font-display font-bold text-xl">Reveal style</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {themes.map((t) => {
              const active = (event.animation_theme || "classic") === t.id;
              return (
                <div key={t.id} data-testid={`theme-${t.id}`} className={`rounded-xl border p-4 transition-colors ${active ? "border-gold bg-gold/10" : "border-white/10 bg-white/5"}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-display font-bold text-lg flex items-center gap-2">
                      {t.name}
                      {active && <Check className="w-4 h-4 text-forest" />}
                    </span>
                    <span className="text-sm font-semibold text-gold">{t.premium ? `$${t.price.toFixed(2)}` : "Free"}</span>
                  </div>
                  <p className="text-sm text-foreground/50 mb-3">{t.desc}</p>
                  <div className="flex gap-2">
                    <Button data-testid={`preview-${t.id}-btn`} size="sm" variant="outline" onClick={() => setPreview(t.id)} className="rounded-full border-white/20 bg-transparent text-xs">
                      <PlayCircle className="w-3.5 h-3.5 mr-1" /> Preview
                    </Button>
                    {active ? (
                      <Button size="sm" disabled className="rounded-full text-xs bg-white/10 text-foreground/60">Active</Button>
                    ) : t.premium ? (
                      <Button data-testid={`unlock-${t.id}-btn`} size="sm" disabled={buying} onClick={() => unlockTheme(t.id)} className="rounded-full text-xs bg-gold hover:bg-gold/90 text-black font-semibold">
                        <Lock className="w-3.5 h-3.5 mr-1" /> Unlock ${t.price.toFixed(2)}
                      </Button>
                    ) : (
                      <Button data-testid={`use-${t.id}-btn`} size="sm" onClick={() => applyFreeTheme(t.id)} className="rounded-full text-xs bg-gold hover:bg-gold/90 text-black font-semibold">Use</Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* reaction gallery / highlight reel */}
        <div className="rounded-2xl bg-card border border-white/5 p-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex items-center gap-2"><Film className="w-5 h-5 text-gold" /><h2 className="font-display font-bold text-xl">Reaction highlight reel</h2></div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-foreground/50">{reactions.length} clip{reactions.length !== 1 ? "s" : ""}</span>
              {reactions.length > 0 && (
                <Button data-testid="generate-highlight-btn" onClick={generateHighlight} disabled={hlStatus === "processing"} size="sm" className="rounded-full bg-gold hover:bg-gold/90 text-black font-semibold">
                  {hlStatus === "processing" ? (<><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Building…</>) : hlUrl ? (<><Film className="w-4 h-4 mr-1.5" /> Rebuild reel</>) : (<><Film className="w-4 h-4 mr-1.5" /> Create highlight reel</>)}
                </Button>
              )}
            </div>
          </div>
          {hlUrl && (
            <div className="mb-6 rounded-2xl border border-gold/20 bg-gold/5 p-4" data-testid="highlight-player">
              <div className="flex items-center gap-2 mb-3 text-gold font-semibold text-sm"><Sparkles className="w-4 h-4" /> Your shareable highlight reel</div>
              <video src={hlUrl} controls playsInline className="w-full rounded-xl border border-white/10 bg-black max-h-[420px]" />
              <div className="flex flex-wrap gap-2 mt-3">
                <Button data-testid="share-reel-btn" onClick={shareReel} className="rounded-full bg-gold hover:bg-gold/90 text-black text-sm font-semibold h-10 px-4"><Share2 className="w-4 h-4 mr-1.5" /> Share to social</Button>
                <a data-testid="download-highlight-btn" href={hlUrl} target="_blank" rel="noopener noreferrer" download className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full bg-white text-black text-sm font-semibold hover:scale-105 transition-transform"><PlayCircle className="w-4 h-4" /> Open / download</a>
                <Button data-testid="copy-highlight-btn" onClick={copyHighlight} variant="outline" size="sm" className="rounded-full border-white/20 bg-transparent h-10"><Copy className="w-4 h-4 mr-1.5" /> Copy video link</Button>
              </div>
            </div>
          )}
          {reactions.length === 0 ? (
            <p className="text-foreground/50 py-8 text-center">Reactions recorded during the reveal will appear here.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="reaction-gallery">
              {reactions.map((r) => (
                <div key={r.id} className="group relative rounded-xl overflow-hidden border border-white/10 bg-black aspect-[9/16] cursor-pointer" onClick={() => setPlaying(r)} data-testid={`reaction-${r.id}`}>
                  <video src={r.video_url || `${API}/reactions/${r.id}/video`} className="w-full h-full object-cover" muted playsInline preload="metadata" />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <PlayCircle className="w-10 h-10 text-white" />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
                    <p className="text-xs text-white truncate">{r.display_name}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {playing && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-xl flex items-center justify-center p-6" onClick={() => setPlaying(null)} data-testid="reaction-player-modal">
          <div className="max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <video src={playing.video_url || `${API}/reactions/${playing.id}/video`} className="w-full rounded-2xl border border-white/10" controls autoPlay />
            <p className="mt-3 text-center text-white flex items-center justify-center gap-2"><CheckCircle2 className="w-4 h-4 text-forest" /> {playing.display_name}'s reaction</p>
          </div>
        </div>
      )}
      <CheckoutOverlay url={checkoutUrl} onClose={() => setCheckoutUrl(null)} />
      {preview && (
        <>
          <RevealAnimation event={{ ...event, animation_theme: preview, revealed: true }} onDone={() => setPreview(null)} />
          <button data-testid="close-preview-btn" onClick={() => setPreview(null)} className="fixed top-4 right-4 z-[130] flex items-center gap-2 px-4 py-2 rounded-full bg-black/60 text-white border border-white/20 hover:bg-black/80">
            <Sparkles className="w-4 h-4" /> Skip preview
          </button>
        </>
      )}
    </div>
  );
}
